'use client';

import { useCallback, useState } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/**
 * Error handling of the dialog's requests, the same rules as the core forms: a 422 goes onto the
 * fields it names, anything else becomes a general message. Core shows the general one as a toast;
 * the toast viewport sits under the dialog overlay, so the step renders it itself (`alert`).
 * Messages for fields the form does not render (`token`, a hidden `email`) are shown as `alert` too.
 */
export function useRequestError<TValues extends FieldValues>(
  form: UseFormReturn<TValues>,
  renderedFields: readonly Path<TValues>[],
) {
  const { t } = useTranslations();
  const [alert, setAlert] = useState<string | null>(null);

  const reportError = useCallback(
    (error: unknown, hint?: string) => {
      if (error instanceof ValidationError) {
        const unmapped: string[] = [];
        let focused = false;
        for (const [field, messages] of Object.entries(error.fields)) {
          const message = messages.join('\n');
          const name = renderedFields.find(rendered => rendered === field);
          if (name) {
            form.setError(name, { type: 'manual', message }, { shouldFocus: !focused });
            focused = true;
          } else {
            unmapped.push(message);
          }
        }
        if (unmapped.length > 0) {
          setAlert(unmapped.join('\n'));
          return;
        }
        if (Object.keys(error.fields).length > 0) return;
      }
      setAlert(hint ? `${t('text651')}\n${hint}` : t('text651'));
    },
    [form, renderedFields, t],
  );

  return { alert, reportError, clearAlert: () => setAlert(null) };
}
